/**
 * Icon lock — one family, one stroke weight, one registry.
 *
 * `lucide-react` is banned in this project. No Figma icon library is wired up
 * for Mentoria yet, so this uses the documented fallback (@phosphor-icons/react)
 * behind the same `<Icon name="…" />` surface. If a Figma set arrives later,
 * only this file and icon.tsx change; no feature code moves.
 *
 * Weight is set once, globally, in icon.tsx via IconContext — never per usage.
 * Adding an icon means adding it here first, which makes a typo a compile error.
 */
import type { Icon as PhosphorIcon } from "@phosphor-icons/react";
import {
  Alarm,
  AirplaneTakeoff,
  ArrowClockwise,
  ArrowDown,
  ArrowRight,
  ArrowUp,
  Bank,
  Bed,
  Briefcase,
  Buildings,
  Cake,
  CalendarBlank,
  CalendarCheck,
  CalendarX,
  CaretDown,
  CaretLeft,
  CaretRight,
  CaretUp,
  CaretUpDown,
  ChartBar,
  ChartLine,
  ChartLineUp,
  ChartPieSlice,
  Check,
  CheckCircle,
  CircleNotch,
  Confetti,
  Copy,
  DotsThree,
  DownloadSimple,
  Envelope,
  Eye,
  EyeSlash,
  FileText,
  FirstAid,
  FloppyDisk,
  FolderOpen,
  Funnel,
  Gear,
  Heartbeat,
  House,
  HouseLine,
  IdentificationCard,
  Info,
  Key,
  List,
  LockKey,
  MagnifyingGlass,
  Minus,
  Moon,
  Note,
  Password,
  Path,
  PencilSimple,
  Phone,
  PlugsConnected,
  Plus,
  PresentationChart,
  Prohibit,
  SealCheck,
  SignOut,
  Sun,
  Table,
  Target,
  Trash,
  Tray,
  UploadSimple,
  UserCircle,
  UserGear,
  UserPlus,
  UsersThree,
  Warning,
  WarningCircle,
  X,
  XCircle,
} from "@phosphor-icons/react";

export const icons = {
  // Navigation
  overview: House,
  calendar: CalendarBlank,
  attendance: Alarm,
  leave: AirplaneTakeoff,
  performance: Target,
  lifecycle: Path,
  recruitment: Briefcase,
  "hiring-dashboard": PresentationChart,
  team: UsersThree,
  sentiment: Heartbeat,
  analytics: ChartLineUp,
  documents: FolderOpen,
  access: Key,
  "user-accounts": UserGear,

  // Chrome
  sun: Sun,
  moon: Moon,
  "sign-out": SignOut,
  menu: List,
  settings: Gear,
  search: MagnifyingGlass,
  filter: Funnel,
  more: DotsThree,

  // Direction
  "caret-down": CaretDown,
  "caret-up": CaretUp,
  "caret-left": CaretLeft,
  "caret-right": CaretRight,
  "caret-sort": CaretUpDown,
  "arrow-up": ArrowUp,
  "arrow-down": ArrowDown,
  "arrow-right": ArrowRight,

  // Actions
  check: Check,
  close: X,
  plus: Plus,
  minus: Minus,
  edit: PencilSimple,
  trash: Trash,
  copy: Copy,
  save: FloppyDisk,
  refresh: ArrowClockwise,
  download: DownloadSimple,
  upload: UploadSimple,
  view: Eye,
  "eye-slash": EyeSlash,
  "add-person": UserPlus,

  // Status
  "check-circle": CheckCircle,
  "x-circle": XCircle,
  warning: Warning,
  "warning-circle": WarningCircle,
  info: Info,
  blocked: Prohibit,
  verified: SealCheck,
  spinner: CircleNotch,
  lock: LockKey,
  password: Password,

  // Data
  "chart-bar": ChartBar,
  "chart-line": ChartLine,
  "chart-pie": ChartPieSlice,
  table: Table,
  empty: Tray,
  unplugged: PlugsConnected,

  // Domain
  birthday: Cake,
  anniversary: Confetti,
  holiday: Bed,
  "calendar-check": CalendarCheck,
  "calendar-x": CalendarX,
  document: FileText,
  note: Note,
  bank: Bank,
  "id-card": IdentificationCard,
  "first-aid": FirstAid,
  email: Envelope,
  phone: Phone,
  org: Buildings,
  // Work from home. HouseLine rather than House, which is already the Overview
  // nav icon — two identical glyphs meaning different things is worse than none.
  wfh: HouseLine,
  person: UserCircle,
} as const satisfies Record<string, PhosphorIcon>;

export type IconName = keyof typeof icons;
